import React from 'react';
import { FaExclamationCircle, FaInfoCircle, FaTimes } from 'react-icons/fa';

const VARIANTS = {
  info: {
    icon: FaInfoCircle,
    ring: 'border-green-200',
    bg: 'bg-green-50',
    iconColor: 'text-green-600',
    titleColor: 'text-green-900',
    bodyColor: 'text-green-800',
    btn: 'bg-green-600 hover:bg-green-700',
  },
  warning: {
    icon: FaExclamationCircle,
    ring: 'border-amber-200',
    bg: 'bg-amber-50',
    iconColor: 'text-amber-600',
    titleColor: 'text-amber-900',
    bodyColor: 'text-amber-800',
    btn: 'bg-amber-600 hover:bg-amber-700',
  },
  error: {
    icon: FaExclamationCircle,
    ring: 'border-red-200',
    bg: 'bg-red-50',
    iconColor: 'text-red-600',
    titleColor: 'text-red-900',
    bodyColor: 'text-red-800',
    btn: 'bg-red-600 hover:bg-red-700',
  },
};

/**
 * In-app alert card — replaces native browser alert() dialogs.
 */
const InfoAlertCard = ({
  open,
  onClose,
  title,
  message,
  children,
  actionLabel = 'OK',
  variant = 'warning',
  onAction,
}) => {
  if (!open) return null;

  const style = VARIANTS[variant] || VARIANTS.warning;
  const Icon = style.icon;

  const handleAction = () => {
    if (onAction) onAction();
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="info-alert-title"
      onClick={onClose}
    >
      <div
        className={`relative w-full max-w-md rounded-2xl border-2 ${style.ring} ${style.bg} shadow-xl`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
          aria-label="Close"
        >
          <FaTimes />
        </button>

        <div className="p-6 pt-7">
          <div className="flex gap-3 pr-6">
            <Icon className={`text-2xl shrink-0 mt-0.5 ${style.iconColor}`} />
            <div className="min-w-0">
              <h3 id="info-alert-title" className={`text-lg font-bold ${style.titleColor}`}>
                {title}
              </h3>
              {message && (
                <p className={`mt-3 text-sm leading-relaxed whitespace-pre-line ${style.bodyColor}`}>
                  {message}
                </p>
              )}
              {children}
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={handleAction}
              className={`rounded-xl px-6 py-2.5 text-sm font-bold text-white ${style.btn}`}
            >
              {actionLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InfoAlertCard;
